// 051 Parto / Aborto -> Observation por grupo bajo el panel `parto-aborto`, más Condition (indicaciones con CIE-10) y Procedure
// (episiotomía, terminación instrumentada o por cesárea). Sin hijos huérfanos: lo que cuelga de un padre en No / Ninguna / n/a no se guarda.
// La fecha efectiva del evento es la de ingreso (grupo del evento) o la del nacimiento (grupo del nacimiento) cuando son AAAA-MM-DD válidas.
import { leerSiNo } from '../camposSimplesDatos';
import { codeDeDiagnostico } from '../SelectorDiagnosticoCie10';
import {
  PRACTICAS_INTERCULTURALES_OPCIONES,
  RECIBIO_PARTO_OPCIONES,
  type EstadoPartoAborto051,
} from '../bloques051PartoDatos';
import type { Observation } from '@medplum/fhirtypes';
import {
  cEntero,
  cFecha,
  cHora,
  cMarca,
  cNumero,
  cOpcion,
  cSiNo,
  cTexto,
  crearCondicion,
  esFechaIso,
  local,
  Seccion051,
  U,
  type Comp,
  type Contexto051,
  type ProcedimientosCreados,
  type Coding,
} from './base';

const P = 'formularios.f051.partoAborto';

export function agregarPartoAborto(ctx: Contexto051, e: EstadoPartoAborto051, procedimientos: ProcedimientosCreados): void {
  const { t } = ctx;
  const s = new Seccion051(ctx, local('parto-aborto', 'Parto / aborto'));
  const op = (code: Coding, clave: string, v: string): Comp => cOpcion(code, v, v ? t(`${P}.${clave}.${v}`) : '');
  const efectivoEn = (fecha: string): Partial<Observation> => (esFechaIso(fecha) ? { effectiveDateTime: fecha.trim() } : {});

  // Evento: tipo, ingreso, consultas y tratamientos antenatales
  s.grupo(
    local('parto-evento', 'Evento obstétrico (parto o aborto)'),
    [
      op(local('tipo-evento', t(`${P}.tipoEvento`)), 'tipoEventoOpciones', e.tipoEvento),
      cFecha(local('fecha-ingreso', t(`${P}.fechaIngreso`)), e.fechaIngreso),
      cEntero(local('consultas-prenatales', t(`${P}.consultasPrenatales`)), e.consultasPrenatales),
      cSiNo(local('hospitalizacion-embarazo', t(`${P}.hospitalizacionEmbarazo`)), e.hospitalizacionEmbarazo),
      e.hospitalizacionEmbarazo !== 'no' ? cNumero(local('hospitalizacion-embarazo-dias', t(`${P}.hospitalizacionEmbarazoDias`)), e.hospitalizacionEmbarazoDias, U.dias) : undefined,
      cSiNo(local('carne-perinatal', t(`${P}.carne`)), e.carne),
      op(local('corticoides-antenatales', t(`${P}.corticoidesAntenatales`)), 'corticoidesAntenatalesOpciones', e.corticoidesAntenatales),
      e.corticoidesAntenatales !== 'ninguna' && e.corticoidesAntenatales !== 'noAplica'
        ? cNumero(local('corticoides-semana-inicio', t(`${P}.corticoidesSemanaInicio`)), e.corticoidesSemanaInicio, U.semanas)
        : undefined,
      op(local('uteroinhibidores', t(`${P}.uteroinhibidores`)), 'uteroinhibidoresOpciones', e.uteroinhibidores),
      e.uteroinhibidores !== 'no' && e.uteroinhibidores !== 'noAplica' ? cNumero(local('uteroinhibidores-semana-inicio', t(`${P}.uteroinhibidoresSemanaInicio`)), e.uteroinhibidoresSemanaInicio, U.semanas) : undefined,
      op(local('inicio-parto', t(`${P}.inicio`)), 'inicioOpciones', e.inicio),
      cNumero(local('eg-semanas', t(`${P}.egSemanas`)), e.egSemanas, U.semanas),
      cNumero(local('eg-dias', t(`${P}.egDias`)), e.egDias, U.dias),
      op(local('eg-metodo', t(`${P}.egMetodo`)), 'egMetodoOpciones', e.egMetodo),
      op(local('presentacion-situacion', t(`${P}.presentacionSituacion`)), 'presentacionSituacionOpciones', e.presentacionSituacion),
      cNumero(local('peso-producto', t(`${P}.pesoProducto`)), e.pesoProducto, U.g),
      cMarca(local('peso-producto-no-aplica', t(`${P}.noAplica`)), e.pesoProductoNoAplica),
      cSiNo(local('tamano-fetal-acorde-eg', t(`${P}.tamanoFetalAcordeEg`)), e.tamanoFetalAcordeEg),
      cSiNo(local('multiple', t(`${P}.multiple`)), e.multiple),
      e.multiple !== 'no' ? cEntero(local('multiple-orden', t(`${P}.multipleOrden`)), e.multipleOrden) : undefined,
    ],
    efectivoEn(e.fechaIngreso)
  );

  // Ruptura de membranas: sin ruptura ("No") no hay fecha, hora, temperatura ni factores de riesgo.
  const rupturaSi = e.rupturaMembranasAnteparto !== 'no';
  s.grupo(
    local('ruptura-membranas-anteparto', t(`${P}.rupturaMembranasAnteparto`)),
    rupturaSi
      ? [
          cFecha(local('ruptura-fecha', t(`${P}.rupturaMembranasFecha`)), e.rupturaMembranasFecha),
          cHora(local('ruptura-hora', t(`${P}.rupturaMembranasHora`)), e.rupturaMembranasHora),
          cNumero(local('ruptura-temperatura', t(`${P}.rupturaMembranasTemperatura`)), e.rupturaMembranasTemperatura, U.celsius),
          cSiNo(local('ruptura-menor-37-semanas', t(`${P}.rupturaMembranasMenor37Semanas`)), e.rupturaMembranasMenor37Semanas),
          cSiNo(local('ruptura-mayor-igual-18-h', t(`${P}.rupturaMembranasMayorIgual18h`)), e.rupturaMembranasMayorIgual18h),
        ]
      : [],
    e.rupturaMembranasAnteparto === 'si' ? { valueBoolean: true } : e.rupturaMembranasAnteparto === 'no' ? { valueBoolean: false } : {}
  );

  // Acompañamiento y prácticas interculturales
  const practicas = PRACTICAS_INTERCULTURALES_OPCIONES.map((c) => cSiNo(local(`practica-${c}`, t(`${P}.practicasInterculturales.opciones.${c}`)), leerSiNo(e.practicasInterculturales[c])));
  // El "cuál" se guarda salvo que todas las prácticas estén contestadas en No.
  const algunaPractica = !PRACTICAS_INTERCULTURALES_OPCIONES.every((c) => leerSiNo(e.practicasInterculturales[c]) === 'no');
  s.grupo(local('parto-acompanamiento-y-practicas', 'Acompañamiento y prácticas interculturales'), [
    cOpcion(local('acompanante-trabajo-parto', t(`${P}.acompananteTdp`)), e.acompananteTdp, e.acompananteTdp ? t(`${P}.acompananteOpciones.${e.acompananteTdp}`) : ''),
    cOpcion(local('acompanante-parto', t(`${P}.acompananteParto`)), e.acompananteParto, e.acompananteParto ? t(`${P}.acompananteOpciones.${e.acompananteParto}`) : ''),
    ...practicas,
    algunaPractica ? cTexto(local('practicas-interculturales-cual', t(`${P}.practicasInterculturalesCual`)), e.practicasInterculturalesCual) : undefined,
  ]);

  // Nacimiento: la fecha del papel es la fecha efectiva del Observation (y queda también como component, por si es lo único que hay).
  s.grupo(
    local('nacimiento', 'Nacimiento'),
    [
      op(local('posicion-parto', t(`${P}.posicionParto`)), 'posicionPartoOpciones', e.posicionParto),
      op(local('nacimiento-establecimiento', t(`${P}.nacimientoEstablecimiento`)), 'nacimientoEstablecimientoOpciones', e.nacimientoEstablecimiento),
      cFecha(local('nacimiento-fecha', t(`${P}.nacimientoFecha`)), e.nacimientoFecha),
      cHora(local('nacimiento-hora', t(`${P}.nacimientoHora`)), e.nacimientoHora),
      op(local('nacimiento-condicion', t(`${P}.nacimientoCondicion`)), 'nacimientoCondicionOpciones', e.nacimientoCondicion),
    ],
    efectivoEn(e.nacimientoFecha)
  );

  // Atención del parto
  s.grupo(local('atencion-parto', 'Atención del parto'), [
    cSiNo(local('episiotomia', t(`${P}.episiotomia`)), e.episiotomia),
    cSiNo(local('desgarro', t(`${P}.desgarro`)), e.desgarro),
    e.desgarro !== 'no' ? cOpcion(local('desgarro-grado', t(`${P}.desgarroGrado`)), e.desgarroGrado, e.desgarroGrado ? `${t(`${P}.desgarroGrado`)} ${e.desgarroGrado}` : '') : undefined,
    cSiNo(local('manejo-activo-alumbramiento', t(`${P}.manejoActivo`)), e.manejoActivo),
    op(local('terminacion-parto', t(`${P}.terminacion`)), 'terminacionOpciones', e.terminacion),
    cTexto(local('indicacion-principal', t(`${P}.indicacionPrincipal`)), e.indicacionPrincipal),
    ...RECIBIO_PARTO_OPCIONES.map((c) => cSiNo(local(`recibio-${c}`, `${t(`${P}.recibio.titulo`)} — ${t(`${P}.recibio.opciones.${c}`)}`), leerSiNo(e.recibio[c]))),
    cSiNo(local('placenta-completa', t(`${P}.placentaCompleta`)), e.placentaCompleta),
    cSiNo(local('placenta-retenida', t(`${P}.placentaRetenida`)), e.placentaRetenida),
    cSiNo(local('ligadura-cordon-al-cesar-pulsaciones', t(`${P}.ligaduraCordonAlCesarPulsaciones`)), e.ligaduraCordonAlCesarPulsaciones),
    cSiNo(local('medicacion', t(`${P}.medicacion`)), e.medicacion),
    e.medicacion !== 'no' ? cTexto(local('medicacion-cual', t(`${P}.medicacionCual`)), e.medicacionCual) : undefined,
  ]);

  s.cerrar();

  // Procedure: lo que se hizo, con la fecha del nacimiento si el papel la trae.
  const fecha = e.nacimientoFecha;
  if (e.episiotomia === 'si') procedimientos.crear(ctx, 'episiotomia', t(`${P}.episiotomia`), fecha);
  if (e.terminacion === 'cesarea' || e.terminacion === 'forceps' || e.terminacion === 'vacuum') procedimientos.crear(ctx, `parto-${e.terminacion}`, t(`${P}.terminacionOpciones.${e.terminacion}`), fecha);

  // Condition: indicaciones con CIE-10 (código si el selector lo resolvió, si no texto libre).
  for (const d of [e.cieInduccion, e.cieOperatoria]) {
    if (d.texto.trim()) crearCondicion(ctx, codeDeDiagnostico(d), fecha);
  }
}
